/**
 * A streaming XML reader for the parts of a Word file (part 18 log, table X; decision 56). It reads the part's bytes in
 * one pass and never holds more of it as a tree than the element its caller takes: for each element the caller says
 * whether to stream through its children, take it whole (its tree handed over when it closes) or skip it. There is no
 * DTD, so no entity can expand and nothing outside the file is ever named; anything that isn't well-formed XML 1.0
 * with namespaces is refused as `damaged`. Pure.
 */
import { ArchiveError } from '../archive/problems.ts';

/** X9: the deepest an element may be nested. */
export const MAX_XML_DEPTH = 256;

/** X10: the most attributes one element may have. */
export const MAX_XML_ATTRIBUTES = 512;

/** X11: the most nodes (elements and texts) one taken tree may have. */
export const MAX_TAKEN_NODES = 200_000;

/** How often, in bytes read, the reader says how far it is (table CO: the page's stall timer). */
export const XML_PROGRESS_BYTES = 1024 * 1024;

/** The XML namespace, bound to `xml:` without a declaration. */
const XML_NS = 'http://www.w3.org/XML/1998/namespace';

/** X8: the Strict OOXML namespaces, read as the transitional ones Word writes by default. */
const STRICT: ReadonlyMap<string, string> = new Map([
  ['http://purl.oclc.org/ooxml/wordprocessingml/main', 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'],
  ['http://purl.oclc.org/ooxml/officeDocument/relationships', 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'],
  ['http://purl.oclc.org/ooxml/drawingml/main', 'http://schemas.openxmlformats.org/drawingml/2006/main'],
  ['http://purl.oclc.org/ooxml/drawingml/picture', 'http://schemas.openxmlformats.org/drawingml/2006/picture'],
  ['http://purl.oclc.org/ooxml/drawingml/wordprocessingDrawing', 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing'],
  ['http://purl.oclc.org/ooxml/officeDocument/math', 'http://schemas.openxmlformats.org/officeDocument/2006/math'],
]);

/** An element: its namespace URI (empty for none), local name, attributes and, once taken, children. */
export interface XmlElement {
  readonly ns: string;
  readonly name: string;
  /** Keyed `{uri}local` for a namespaced attribute, `local` for one without (Clark notation). */
  readonly attributes: ReadonlyMap<string, string>;
  /** Elements and texts in order; empty for an element streamed through. */
  readonly children: readonly XmlNode[];
}

/** A taken tree's node: an element or a text. */
export type XmlNode = XmlElement | string;

/** X12: what the reader does with an element it opens outside any taken tree. */
export type XmlVisit = 'descend' | 'take' | 'skip';

/** What the reader tells its caller. */
export interface XmlHandler {
  /** An element opened outside any taken tree, `depth` 0 for the root. */
  open(element: XmlElement, depth: number): XmlVisit;
  /** A taken element, whole, as it closes. */
  take?(element: XmlElement, depth: number): void;
  /** An element streamed through, as it closes. */
  close?(element: XmlElement, depth: number): void;
  /** Bytes of the part read so far, at least every {@link XML_PROGRESS_BYTES}. */
  progress?(bytes: number): void;
}

/** `{uri}local`, the key of a namespaced attribute; `local` alone with no namespace. */
export function attributeKey(ns: string, local: string): string {
  return ns === '' ? local : `{${ns}}${local}`;
}

/** The attribute `local` of namespace `ns` on `element`, or undefined. */
export function attribute(element: XmlElement, ns: string, local: string): string | undefined {
  return element.attributes.get(attributeKey(ns, local));
}

/** The child elements of `element`. */
export function childElements(element: XmlElement): XmlElement[] {
  return element.children.filter((child) => typeof child !== 'string');
}

/** The first child element of `element` named `name` in `ns`, or undefined. */
export function child(element: XmlElement, ns: string, name: string): XmlElement | undefined {
  for (const node of element.children) if (typeof node !== 'string' && node.ns === ns && node.name === name) return node;
  return undefined;
}

/** The text an element holds, its descendants' included. */
export function textOf(element: XmlElement): string {
  let out = '';
  for (const node of element.children) out += typeof node === 'string' ? node : textOf(node);
  return out;
}

const UTF8 = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });

function damaged(message: string): ArchiveError {
  return new ArchiveError('damaged', `XML: ${message}`);
}

/** X1: a part's bytes as UTF-8: a UTF-8 byte-order mark dropped, UTF-16 (by its mark) decoded and encoded again. */
export function xmlBytes(raw: Uint8Array): Uint8Array {
  if (raw[0] === 0xef && raw[1] === 0xbb && raw[2] === 0xbf) return raw.subarray(3);
  const utf16 = raw[0] === 0xff && raw[1] === 0xfe ? 'utf-16le' : raw[0] === 0xfe && raw[1] === 0xff ? 'utf-16be' : null;
  if (utf16 === null) return raw;
  try {
    return new TextEncoder().encode(new TextDecoder(utf16, { fatal: true, ignoreBOM: false }).decode(raw));
  } catch {
    throw damaged('UTF-16 that does not decode');
  }
}

const LT = 0x3c;
const GT = 0x3e;
const AMP = 0x26;
const SLASH = 0x2f;
const EQUALS = 0x3d;
const QUOTE = 0x22;
const APOSTROPHE = 0x27;

function isSpace(byte: number | undefined): boolean {
  return byte === 0x20 || byte === 0x09 || byte === 0x0a || byte === 0x0d;
}

/** X7: a name's bytes: letters, digits, `_`, `:`, `.`, `-` and every byte of a non-ASCII character. */
function isNameByte(byte: number): boolean {
  return (byte >= 0x61 && byte <= 0x7a) || (byte >= 0x41 && byte <= 0x5a) || (byte >= 0x30 && byte <= 0x3a) || byte === 0x5f || byte === 0x2e || byte === 0x2d || byte >= 0x80;
}

/** The predefined entities (X5). */
const ENTITIES: ReadonlyMap<string, string> = new Map([
  ['lt', '<'],
  ['gt', '>'],
  ['amp', '&'],
  ['quot', '"'],
  ['apos', "'"],
]);

/** X5: a character reference's code point, if it names an XML character. */
function characterOf(code: number): string {
  const allowed = code === 0x9 || code === 0xa || code === 0xd || (code >= 0x20 && code <= 0xd7ff) || (code >= 0xe000 && code <= 0xfffd) || (code >= 0x10000 && code <= 0x10ffff);
  if (!allowed) throw damaged(`a character reference to ${code}`);
  return String.fromCodePoint(code);
}

/** X5: one entity's text, `body` what stands between `&` and `;`. */
function entity(body: string): string {
  const known = ENTITIES.get(body);
  if (known !== undefined) return known;
  if (/^#x[0-9a-fA-F]{1,8}$/u.test(body)) return characterOf(Number.parseInt(body.slice(2), 16));
  if (/^#[0-9]{1,10}$/u.test(body)) return characterOf(Number.parseInt(body.slice(1), 10));
  throw damaged(`an unknown entity ${JSON.stringify(body.slice(0, 40))}`);
}

/** X2, X5: bytes of text or an attribute value, decoded, entities applied. */
function decodeText(bytes: Uint8Array, from: number, to: number): string {
  // Every search is within the text's own bytes: a search to the part's end for each text would be quadratic.
  const text = bytes.subarray(from, to);
  let out = '';
  let at = 0;
  while (at < text.length) {
    const amp = text.indexOf(AMP, at);
    const end = amp < 0 ? text.length : amp;
    if (end > at) out += decode(text, at, end);
    if (amp < 0) break;
    const semicolon = text.subarray(amp, amp + 14).indexOf(0x3b);
    // An entity's name is short: one that runs on is no entity.
    if (semicolon < 0) throw damaged('an unterminated entity');
    out += entity(decode(text, amp + 1, amp + semicolon));
    at = amp + semicolon + 1;
  }
  return out;
}

function decode(bytes: Uint8Array, from: number, to: number): string {
  try {
    return UTF8.decode(bytes.subarray(from, to));
  } catch {
    throw damaged('text that is not UTF-8');
  }
}

/** A name in a tag: split at its prefix. */
interface QName {
  readonly raw: string;
  readonly prefix: string;
  readonly local: string;
}

function qname(raw: string): QName {
  const colon = raw.indexOf(':');
  if (colon === 0 || colon === raw.length - 1 || raw.includes(':', colon + 1)) throw damaged(`a name ${JSON.stringify(raw.slice(0, 40))}`);
  return colon < 0 ? { raw, prefix: '', local: raw } : { raw, prefix: raw.slice(0, colon), local: raw.slice(colon + 1) };
}

/** One open element on the reader's stack. */
interface Frame {
  readonly raw: string;
  readonly scope: ReadonlyMap<string, string>;
  /** `inside`: an element of a tree being taken, which goes into `into` (its parent's children) as it closes. */
  readonly visit: XmlVisit | 'inside';
  readonly element: XmlElement;
  /** Its children while its tree is built (taken or inside one); for any other, never filled. */
  readonly children: XmlNode[];
  readonly into: XmlNode[] | null;
}

/** Whether a frame's children are kept: it is taken, or inside a taken tree. */
function building(frame: Frame): boolean {
  return frame.visit === 'take' || frame.visit === 'inside';
}

/** The namespace a prefix names in `scope`; X8: an undeclared one is refused. */
function resolve(scope: ReadonlyMap<string, string>, prefix: string): string {
  if (prefix === 'xml') return XML_NS;
  const uri = scope.get(prefix);
  if (uri === undefined) {
    if (prefix === '') return '';
    throw damaged(`an undeclared prefix ${JSON.stringify(prefix.slice(0, 40))}`);
  }
  return STRICT.get(uri) ?? uri;
}

/**
 * Table X: reads `bytes` (a part, after {@link xmlBytes}) and tells `handler` what it finds. Throws an ArchiveError,
 * `damaged` for XML it refuses, `too-big` for a taken tree over {@link MAX_TAKEN_NODES}.
 */
export function readXml(bytes: Uint8Array, handler: XmlHandler): void {
  const stack: Frame[] = [];
  let at = 0;
  // An object, not a variable: the nested readers set it, which a variable's narrowing wouldn't see.
  const found = { root: false };
  let nodes = 0;
  let reported = 0;
  const length = bytes.length;
  const report = (): void => {
    if (at - reported >= XML_PROGRESS_BYTES) {
      reported = at;
      handler.progress?.(at);
    }
  };
  const top = (): Frame | undefined => stack.at(-1);
  const addNode = (into: XmlNode[], node: XmlNode): void => {
    nodes += 1;
    if (nodes > MAX_TAKEN_NODES) throw new ArchiveError('too-big', `an element of more than ${MAX_TAKEN_NODES} nodes`);
    into.push(node);
  };
  const text = (from: number, to: number, raw: boolean): void => {
    const frame = top();
    if (frame === undefined) {
      for (let i = from; i < to; i++) if (!isSpace(bytes[i])) throw damaged('text outside the root');
      return;
    }
    if (!building(frame) || to === from) return;
    addNode(frame.children, raw ? decode(bytes, from, to) : decodeText(bytes, from, to));
  };
  while (at < length) {
    report();
    const lt = bytes.indexOf(LT, at);
    const end = lt < 0 ? length : lt;
    text(at, end, false);
    if (lt < 0) break;
    at = lt;
    const next = bytes[at + 1];
    if (next === 0x3f) {
      // X3: a processing instruction or the declaration.
      const close = indexOfSequence(bytes, '?>', at + 2);
      if (close < 0) throw damaged('an unterminated processing instruction');
      at = close + 2;
    } else if (next === 0x21) {
      if (startsWith(bytes, at, '<!--')) {
        const close = indexOfSequence(bytes, '-->', at + 4);
        if (close < 0) throw damaged('an unterminated comment');
        at = close + 3;
      } else if (startsWith(bytes, at, '<![CDATA[')) {
        if (stack.length === 0) throw damaged('CDATA outside the root');
        const close = indexOfSequence(bytes, ']]>', at + 9);
        if (close < 0) throw damaged('unterminated CDATA');
        text(at + 9, close, true);
        at = close + 3;
      } else {
        // X4: a DTD, an entity or anything else of the kind.
        throw damaged('a document type or declaration');
      }
    } else if (next === SLASH) {
      at = endTag(at + 2);
    } else {
      at = startTag(at + 1);
    }
  }
  if (stack.length > 0) throw damaged('an element left open');
  if (!found.root) throw damaged('no root element');
  handler.progress?.(length);

  /** Reads a name from `from`; returns it and where it ends. */
  function nameAt(from: number): [string, number] {
    const end = bytes.subarray(from).findIndex((byte) => !isNameByte(byte));
    const i = end < 0 ? length : from + end;
    if (i === from) throw damaged('a tag without a name');
    return [decode(bytes, from, i), i];
  }

  function skipSpace(from: number): number {
    let i = from;
    while (isSpace(bytes[i])) i++;
    return i;
  }

  function endTag(from: number): number {
    const [raw, after] = nameAt(from);
    const close = skipSpace(after);
    if (bytes[close] !== GT) throw damaged('a broken end tag');
    closeTop(raw);
    return close + 1;
  }

  /** Closes the open element, which must be `raw`, and hands it on as its visit says. */
  function closeTop(raw: string): void {
    const frame = stack.pop();
    if (frame?.raw !== raw) throw damaged(`an end tag ${JSON.stringify(raw.slice(0, 40))} that closes nothing open`);
    const depth = stack.length;
    if (frame.visit === 'take') {
      nodes = 0;
      handler.take?.(withChildren(frame), depth);
    } else if (frame.into !== null) {
      addNode(frame.into, withChildren(frame));
    } else if (frame.visit === 'descend') {
      handler.close?.(frame.element, depth);
    }
  }

  function startTag(from: number): number {
    if (found.root && stack.length === 0) throw damaged('a second root element');
    if (stack.length >= MAX_XML_DEPTH) throw damaged(`elements nested deeper than ${MAX_XML_DEPTH}`);
    const [raw, afterName] = nameAt(from);
    const pairs: [QName, string][] = [];
    const seen = new Set<string>();
    let i = afterName;
    for (;;) {
      const spaced = skipSpace(i);
      const byte = bytes[spaced];
      if (byte === GT || byte === SLASH) {
        i = spaced;
        break;
      }
      if (spaced === i) throw damaged('attributes run together');
      const [name, afterAttribute] = nameAt(spaced);
      if (seen.has(name)) throw damaged(`an attribute ${JSON.stringify(name.slice(0, 40))} given twice`);
      seen.add(name);
      if (seen.size > MAX_XML_ATTRIBUTES) throw damaged(`more than ${MAX_XML_ATTRIBUTES} attributes`);
      let k = skipSpace(afterAttribute);
      if (bytes[k] !== EQUALS) throw damaged('an attribute without a value');
      k = skipSpace(k + 1);
      const quote = bytes[k];
      if (quote !== QUOTE && quote !== APOSTROPHE) throw damaged('an unquoted attribute');
      const close = bytes.indexOf(quote, k + 1);
      if (close < 0) throw damaged('an unterminated attribute');
      if (bytes.subarray(k + 1, close).includes(LT)) throw damaged('`<` in an attribute value');
      pairs.push([qname(name), decodeText(bytes, k + 1, close)]);
      i = close + 1;
    }
    const selfClosing = bytes[i] === SLASH;
    if (selfClosing && bytes[i + 1] !== GT) throw damaged('a broken empty-element tag');
    const after = selfClosing ? i + 2 : i + 1;
    const parent = top();
    let scope = parent?.scope ?? new Map<string, string>();
    const declared = pairs.filter(([name]) => name.raw === 'xmlns' || name.prefix === 'xmlns');
    if (declared.length > 0) {
      const own = new Map(scope);
      for (const [name, value] of declared) own.set(name.raw === 'xmlns' ? '' : name.local, value);
      scope = own;
    }
    const tag = qname(raw);
    const attributes = new Map<string, string>();
    for (const [name, value] of pairs) {
      if (name.raw === 'xmlns' || name.prefix === 'xmlns') continue;
      attributes.set(attributeKey(name.prefix === '' ? '' : resolve(scope, name.prefix), name.local), value);
    }
    const element: XmlElement = { ns: resolve(scope, tag.prefix), name: tag.local, attributes, children: [] };
    found.root = true;
    const depth = stack.length;
    let visit: Frame['visit'];
    let into: XmlNode[] | null = null;
    if (parent === undefined || parent.visit === 'descend') visit = handler.open(element, depth);
    else if (parent.visit === 'skip') visit = 'skip';
    else {
      visit = 'inside';
      into = parent.children;
    }
    const frame: Frame = { raw, scope, visit, element, children: [], into };
    if (visit === 'take') nodes = 1;
    stack.push(frame);
    if (selfClosing) closeTop(raw);
    return after;
  }
}

/** A built frame's element with its children. */
function withChildren(frame: Frame): XmlElement {
  return { ...frame.element, children: frame.children };
}

function startsWith(bytes: Uint8Array, at: number, text: string): boolean {
  for (let i = 0; i < text.length; i++) if (bytes[at + i] !== text.charCodeAt(i)) return false;
  return true;
}

/** Where the ASCII `text` next starts in `bytes` from `from`, or -1. */
function indexOfSequence(bytes: Uint8Array, text: string, from: number): number {
  const first = text.charCodeAt(0);
  for (let at = bytes.indexOf(first, from); at >= 0; at = bytes.indexOf(first, at + 1)) if (startsWith(bytes, at, text)) return at;
  return -1;
}
